import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Button, Grid, Box, Card, CardContent, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { t } from 'i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    deleteNutritionPlan,
    fetchNutritionPlanByUUID
} from '../../../services/nutrition';
import NutriPlanViewer from './NutriPlanViewer';
import BulkAssignNutritionDialog from './BulkAssignNutritionDialog';

const NutriPlanDetails = () => {
    const { uuid } = useParams();
    const theme = useTheme();
    const navigate = useNavigate();
    const [plan, setPlan] = useState(null);
    const [openAssignDialog, setOpenAssignDialog] = useState(false);

    useEffect(() => {
        const fetchPlan = async () => {
            try {
                const data = await fetchNutritionPlanByUUID(uuid);

                setPlan({
                    uuid: data.uuid,
                    title: data.name,
                    description: data.description,
                    durationDays: data.data?.durationDays,
                    days: data.data?.days || [],
                    foodsToAvoid: data.data?.foodsToAvoid || [],
                    customNotes: data.data?.customNotes || []
                });
            } catch (error) {
                console.error('Erro ao carregar plano de nutrição:', error);
            }
        };

        fetchPlan();
    }, [uuid]);

    if (!plan) return <Typography>{t('nutrition.loading_plan')}</Typography>;

    return (
        <Card sx={{ m: 2 }}>
            <CardContent>
                <Box display="flex" gap={2}>
                    <Button variant="contained" onClick={() => setOpenAssignDialog(true)}>
                        {t('plans.assign')}
                    </Button>
                </Box>
                <BulkAssignNutritionDialog
                    open={openAssignDialog}
                    onClose={() => setOpenAssignDialog(false)}
                    planUUIDs={uuid}
                />

                <Divider sx={{ mt: 2 }} />

                <Box sx={{ m: 3 }}>
                    <Card
                        sx={{
                            borderRadius: 3,
                            boxShadow: 3,
                            p: 3,
                            backgroundColor: '#fafafa'
                        }}
                    >
                        <Grid item xs={12}>
                            <Typography
                                variant="h1"
                                align="center"
                                gutterBottom
                                sx={{ color: theme.palette.primary.dark }}
                            >
                                {plan.title}
                            </Typography>

                            <Typography
                                variant="body1"
                                align="center"
                                gutterBottom
                                sx={{ fontSize: '18px' }}
                            >
                                {plan.description}
                            </Typography>

                            <Typography
                                variant="body1"
                                align="center"
                                gutterBottom
                                sx={{ fontSize: '14px' }}
                            >
                                {t('nutrition.duration')} {plan.durationDays}{' '}
                                {t('nutrition.days')}
                            </Typography>
                        </Grid>

                        <NutriPlanViewer
                            days={plan.days}
                            foodsToAvoid={plan.foodsToAvoid}
                            customNotes={plan.customNotes}
                        />

                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mt={4}
                        >
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={async () => {
                                    if (
                                        window.confirm(t('plansDetails.delete_confirm'))
                                    ) {
                                        try {
                                            await deleteNutritionPlan(uuid);
                                            navigate('/nutricao');
                                        } catch (error) {
                                            console.error(
                                                t('plansDetails.delete_error'),
                                                error
                                            );
                                            alert(t('plansDetails.delete_error'));
                                        }
                                    }
                                }}
                            >
                                {t('plansDetails.delete_plan')}
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => navigate(`/nutricao/duplicar/${uuid}`)}
                            >
                                {t('plansDetails.duplicate_plan')}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/nutricao')}
                            >
                                {t('plansDetails.back')}
                            </Button>
                        </Box>
                    </Card>
                </Box>
            </CardContent>
        </Card>
    );
};

export default NutriPlanDetails;
